import router, { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import { Table } from "common/ui/Table";
import { Stack } from "common/ui/Stack";
import { Paragraph } from "common/ui/Paragraph";
import { Button } from "common/ui/Button";
import { Icon } from "common/ui/Icon";
import { ProgressBar } from "common/ui/ProgressBar";
import { ListingStatusPill } from "common/elements/ListingStatusPill";

import { currencyFormatLocale, formatDateMonthYear } from "common/utils/utils";

import { ListingLookupDto, ListingTier } from "common/middleware/api.services";
import { ListingStep } from "common/middleware/types.properties";
import { toEnumValue } from "common/utils/toEnumValue";
import { SELLER_ROUTES } from "common/constants/routes";

interface DraftListingProps {
  listings: ListingLookupDto[],
  setIdOfListingToDelete: (value: number) => void,
  setIdOfListingToArchive: (propertyId: number) => void,
}

export function DraftListingsTable({
  listings,
  setIdOfListingToDelete,
  setIdOfListingToArchive,
}: DraftListingProps) {
  const { locale } = useRouter();
  const { t } = useTranslation("admin");

  const rows = listings?.map((listing) => {
    const listingState = listing.state.toString();
    const isDraftOfActiveProperty = Boolean(listing.parentListingId);
    const isStandardListing = toEnumValue(ListingTier, listing.tier) === ListingTier.Standard;
    const isCurveballListing = toEnumValue(ListingTier, listing.tier) === ListingTier.Curveball;

    const address = (
      <>
        <Paragraph variant="p1">
          {listing.address.unitNumber}
          {" "}
          {listing.address.street}
        </Paragraph>
        <Paragraph variant="p1">
          {listing.address.city}
          {" "}
          {listing.address.province}
          {" "}
          {listing.address.postalCode}
        </Paragraph>
        <Paragraph variant="p1">
          {listing.address.country}
        </Paragraph>
      </>
    );

    const listPrice = (
      <Paragraph variant="p1">
        {currencyFormatLocale(listing.price, locale)}
      </Paragraph>
    );

    const status = (
      <ListingStatusPill
        status={isDraftOfActiveProperty && listingState === "incomplete"
          ? "draft"
          : listingState
        }
      />
    );

    const progress = (isStandardListing || isDraftOfActiveProperty) ? (
      <div style={{ width: `172px` }}>
        <ProgressBar
          numSteps={Object.keys(ListingStep).length / 2}
          currStep={Number(listing.lastCompletedListingStep) || 0}
        />
      </div>
    ) : (
      t("shared.na")
    );

    const date = (
      <Paragraph variant="p1">
        {listingState === "denied" ? (
          t("draftListingsTable.deniedOn", {
            date: formatDateMonthYear(locale, listing.approvedOrDeniedListingDateTimeUtc)
          })
        ) : listingState === "pending" ? (
          t("draftListingsTable.submittedOn", {
            date: formatDateMonthYear(locale, listing.submittedDateTimeUtc)
          })
        ) : (
          t("draftListingsTable.lastSavedOn", {
            date: formatDateMonthYear(locale, listing.lastSavedDateTimeUtc)
          })
        )}
      </Paragraph>
    );

    const quickActions = (
      <Stack direction="row" gap={16}>
        {(listingState === "denied" && (isStandardListing || isDraftOfActiveProperty || isCurveballListing)) && (
          <Button
            variant="tertiary"
            noIndent
            onClick={(e) => {
              e.stopPropagation();

              if (isCurveballListing) {
                router.push({
                  pathname: SELLER_ROUTES.CREATE_LISTING.REVIEW,
                  query: { propertyId: listing.id },
                });
                return;
              }

              router.push({
                pathname: `/listings/review`,
                query: {
                  propertyId: isDraftOfActiveProperty ? listing.parentListingId : listing.id,
                  ...(isDraftOfActiveProperty ? { status: "draft" } : {}),
                },
              });
            }}
          >
            {t("draftListingsTable.fixErrors")}
          </Button>
        )}
        {listingState !== "incomplete" && (
          <Button
            variant="inline"
            onClick={(e) => {
              e.stopPropagation();
              router.push(`/listings/${listing.id}`);
            }}
            title={t("shared.preview")}
          >
            <Icon icon="eye" />
          </Button>
        )}
        {(
          listingState === "incomplete"
          || isDraftOfActiveProperty
        ) ? (
          <Button
            variant="inline"
            onClick={(e) => {
              e.stopPropagation();
              setIdOfListingToDelete(isDraftOfActiveProperty
                ? listing.parentListingId
                : listing.id
              );
            }}
            title={t("shared.delete")}
          >
            <Icon icon="trash" />
          </Button>
        ) : (!isDraftOfActiveProperty && listingState !== "pending") ? (
          <Button
            variant="inline"
            onClick={(e) => {
              e.stopPropagation();
              setIdOfListingToArchive(listing.id);
            }}
            title={t("shared.archive")}
          >
            <Icon icon="archive" />
          </Button>
        ) : null}
      </Stack>
    );

    const handleClick = () => {
      if (isDraftOfActiveProperty) {
        router.push({
          pathname: isStandardListing ? "/listings/review" : "/listings/property",
          query: {
            propertyId: listing.parentListingId,
            status: "draft",
          },
        });
        return;
      }

      if (isCurveballListing) {
        router.push({
          pathname: SELLER_ROUTES.CREATE_LISTING.OWNERSHIP.OVERVIEW,
          query: { propertyId: listing.id },
        });
        return;
      }

      router.push({
        pathname: "/listings/external",
        query: { propertyId: listing.id },
      });
    };

    return {
      id: listing.id,
      items: [
        address,
        listPrice,
        status,
        progress,
        date,
        quickActions
      ],
      onClick: (listingState === "incomplete" || (isDraftOfActiveProperty && listingState !== "pending"))
        ? handleClick
        : undefined
    }
  });

  return (
    <Table
      headers={[
        t("common.address"),
        t("common.listPrice"),
        t("draftListingsTable.approvalStatus"),
        t("draftListingsTable.progress"),
        t("common.date"),
        ""
      ]}
      rows={rows}
    />
  )
}
